import type { ReactNode } from 'react';
import styled from 'styled-components';
import { MetaRow } from '@/ui';
import { useCases, useCouncilOverview } from '@/hooks';
import { pipelineStages } from '@/core/data/council';
import type { CouncilStat, DailyReport } from '@/core/data/council';
import { pendingCases } from '../filterCases';
import { CaseList } from '../components/CaseList';
import { COUNCIL_KEEP, COUNCIL_REMOVE } from '../tones';

// ── Stat cards ──────────────────────────────────────────────────────────────

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;
  margin-bottom: 16px;
  @media (max-width: 980px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const Stat = styled.div<{ $tone: string }>`
  padding: 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme, $tone }) => `color-mix(in srgb, ${$tone} 12%, ${theme.color.glassBg})`};
  .v {
    font-size: ${({ theme }) => theme.fontSize.lg};
    font-weight: 700;
    color: ${({ theme }) => theme.color.textPrimary};
  }
  .l {
    font-size: ${({ theme }) => theme.fontSize.xs};
    color: ${({ theme }) => theme.color.textMuted};
  }
  .s {
    font-size: ${({ theme }) => theme.fontSize.xs};
    color: ${({ theme }) => theme.color.textMuted};
  }
`;

const TONE_HEX: Record<string, string> = {
  blue: '#4b8dff',
  green: '#36e0a0',
  violet: '#a13bff',
  cyan: '#2fd9e0',
  amber: '#ffb13b',
  pink: '#ff5ba6',
};

// ── Pipeline ─────────────────────────────────────────────────────────────────

const Pipeline = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  margin-bottom: 16px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  @media (max-width: 720px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Stage = styled.div`
  flex: 1;
  .nm {
    color: ${({ theme }) => theme.color.textPrimary};
    font-weight: 600;
  }
  .bl {
    font-size: ${({ theme }) => theme.fontSize.xs};
    color: ${({ theme }) => theme.color.textMuted};
  }
`;

const Arrow = styled.span`
  color: ${({ theme }) => theme.color.accentCyan};
  flex: 0 0 auto;
`;

// ── Team roster ───────────────────────────────────────────────────────────────

const Roster = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 16px;
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Team = styled.div`
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  .tn {
    color: ${({ theme }) => theme.color.textPrimary};
    font-weight: 600;
  }
  .tl {
    font-size: ${({ theme }) => theme.fontSize.xs};
    color: ${({ theme }) => theme.color.textMuted};
  }
`;

const Members = styled.div`
  display: flex;
  gap: 4px;
  margin: 8px 0;
`;

const Av = styled.span`
  width: 26px;
  height: 26px;
  border-radius: ${({ theme }) => theme.radius.sm};
  display: grid;
  place-items: center;
  font-size: 11px;
  color: ${({ theme }) => theme.color.onAccent};
  background: ${({ theme }) => theme.color.brandFrom};
`;

// ── Bottom grid ───────────────────────────────────────────────────────────────

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 16px;
  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  margin-bottom: 12px;
  h3 {
    margin: 0 0 8px;
    font-size: ${({ theme }) => theme.fontSize.sm};
    color: ${({ theme }) => theme.color.textPrimary};
  }
`;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  padding: 3px 0;
  b {
    color: ${({ theme }) => theme.color.textPrimary};
  }
`;

const QueueTitle = styled.h3`
  margin: 0 0 8px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Outcome = styled.span<{ $removed: boolean }>`
  color: ${({ $removed }) => ($removed ? COUNCIL_REMOVE : COUNCIL_KEEP)};
`;

// ── Component ─────────────────────────────────────────────────────────────────

export function OverviewSection() {
  const { cases } = useCases();
  const pending = pendingCases(cases);
  const overview = useCouncilOverview();
  const ov = overview?.stats;

  // Real/derived stat cards; Avg Resolve Time + Consistency have no real source → static.
  const stats: CouncilStat[] = [
    { label: 'Pending Cases', value: String(ov?.pendingCases ?? pending.length), sub: 'live', tone: 'blue' },
    { label: 'Resolved Today', value: String(ov?.resolvedToday ?? 0), sub: 'decisions today', tone: 'green' },
    { label: 'Active Teams', value: String(ov?.activeTeams ?? 0), sub: 'rotating', tone: 'violet' },
    { label: 'Avg Resolve Time', value: '2h 14m', sub: 'illustrative', tone: 'cyan' },
    { label: 'Open Appeals', value: String(ov?.openAppeals ?? 0), sub: 'in review', tone: 'amber' },
    { label: 'Consistency', value: '96%', sub: 'illustrative', tone: 'pink' },
  ];
  const realTeams = overview?.teams ?? [];
  const recent = overview?.recentDecisions ?? [];
  const appeals = overview?.appeals ?? [];
  // Every row here must come off the API. This panel used to interleave two real
  // values with two invented ones — "AI-cleared 1,231" and "Escalated to humans 611"
  // — carrying no marker to tell them apart, which made the real figures beside them
  // read as invented too. They are removed rather than zeroed: there is no AI triage
  // on this platform, so "AI-cleared 0" would still describe something that does not
  // exist.
  const reportsRows: DailyReport[] = [
    { label: 'Reports filed', value: String(ov?.reportsFiled ?? 0) },
    { label: 'Removed', value: String(ov?.removed ?? 0) },
  ];

  // Build interleaved pipeline: [stage, arrow, stage, arrow, stage]
  const pipelineNodes: ReactNode[] = pipelineStages.flatMap((p, i) => {
    const count = p.id === 'human-review' ? (ov?.humanReview ?? p.count) : p.count;
    const stage = (
      <Stage key={p.id}>
        <div className="nm">
          <span>{p.name}</span>
          <span> {count}</span>
        </div>
        <div className="bl">{p.blurb}</div>
      </Stage>
    );
    return i < pipelineStages.length - 1
      ? [stage, <Arrow key={`arrow-${i}`} aria-hidden>→</Arrow>]
      : [stage];
  });

  return (
    <div>
      {/* Stat cards */}
      <Stats>
        {stats.map((s) => (
          <Stat key={s.label} $tone={TONE_HEX[s.tone] ?? '#4b8dff'}>
            <div className="v">{s.value}</div>
            <div className="l">{s.label}</div>
            <div className="s">{s.sub}</div>
          </Stat>
        ))}
      </Stats>

      {/* Moderation pipeline */}
      <Pipeline>{pipelineNodes}</Pipeline>

      {/* Team roster */}
      <Roster>
        {realTeams.map((t) => (
          <Team key={t.id}>
            <MetaRow block className="tn">
              <span>{t.name}</span>
              <span>{t.status}</span>
            </MetaRow>
            <Members>
              {t.members.map((m, i) => (
                <Av key={`${t.id}-${i}`} title={m.name}>
                  {m.name[0]}
                </Av>
              ))}
            </Members>
            <div className="tl">{t.caseload} active cases</div>
          </Team>
        ))}
      </Roster>

      {/* Bottom grid: live queue + side panels */}
      <Grid>
        <div>
          <QueueTitle>Human Review Queue</QueueTitle>
          <CaseList cases={pending} emptyLabel="No cases awaiting review." />
        </div>
        <div>
          <Panel>
            <h3>Daily Reports</h3>
            {reportsRows.map((r) => (
              <MetricRow key={r.label}>
                <span>{r.label}</span>
                <b>{r.value}</b>
              </MetricRow>
            ))}
          </Panel>
          <Panel>
            <h3>Recent Decisions</h3>
            {recent.map((d) => (
              <MetricRow key={d.caseId}>
                <span>{d.summary}</span>
                <Outcome $removed={d.outcome === 'removed'}>
                  {d.outcome === 'removed' ? 'Removed' : 'Kept'}
                </Outcome>
              </MetricRow>
            ))}
          </Panel>
          <Panel>
            <h3>Appeals &amp; Bias Reviews</h3>
            {appeals.map((a) => (
              <MetricRow key={a.caseId}>
                <span>{a.summary}</span>
                <b>{a.status}</b>
              </MetricRow>
            ))}
          </Panel>
        </div>
      </Grid>
    </div>
  );
}
